import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { PageProps } from '../types/auth';
import styles from './styles/GroupSnippetsPage.module.css';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

interface Group {
  group_id: number;
  name: string;
  curriculum_id?: number;
}

interface Curriculum {
  curriculum_id: number;
  name: string;
}

interface RouteParams {
  [key: string]: string | undefined;
  group_id?: string;
}

const GroupSnippetsPage: React.FC<PageProps> = ({ token, selectedOrgID }) => {
  const { group_id } = useParams<RouteParams>();
  const navigate = useNavigate();
  const [groupName, setGroupName] = useState<string>("");
  const [curriculums, setCurriculums] = useState<Curriculum[]>([]);
  const [selectedCurriculum, setSelectedCurriculum] = useState<string>('');
  const [periodStart, setPeriodStart] = useState<string>('');
  const [periodEnd, setPeriodEnd] = useState<string>('');
  const [error, setError] = useState<string>('');
  const [curriculumError, setCurriculumError] = useState<string>('');
  const [dateError, setDateError] = useState<string>('');

  // Set default dates (from: 30 days ago, to: today)
  useEffect(() => {
    const today = new Date();
    const defaultStartDate = new Date();
    defaultStartDate.setDate(today.getDate() - 7);
    
    setPeriodEnd(today.toISOString().split('T')[0]);
    setPeriodStart(defaultStartDate.toISOString().split('T')[0]);
  }, []);

  useEffect(() => {
    const fetchGroupDetails = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/api/groups/${group_id}`, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
            "X-Org-ID": selectedOrgID?.toString() || "",
          },
        });

        if (!response.ok) {
          const errorData = await response.json();
          setError(errorData.error || "Failed to fetch group details.");
          return;
        }

        const data: Group = await response.json();
        setGroupName(data.name || "");
        if (data.curriculum_id) {
          setSelectedCurriculum(data.curriculum_id.toString());
        }
      } catch (err) {
        setError("An unexpected error occurred while fetching group details.");
      }
    };

    const fetchCurriculums = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/api/curriculums`, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
            "X-Org-ID": selectedOrgID?.toString() || "",
          },
        });

        if (!response.ok) {
          const errorData = await response.json();
          setError(errorData.error || "Failed to fetch curriculums.");
          return;
        }

        const data: Curriculum[] = await response.json();
        setCurriculums(data || []);
      } catch (err) {
        setError("An unexpected error occurred while fetching curriculums.");
      }
    };

    if (group_id) {
      fetchGroupDetails();
      fetchCurriculums();
    }
  }, [group_id, token, selectedOrgID]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Clear any previous errors
    setError("");
    setDateError("");
    setCurriculumError("");
    
    let hasError = false;

    if (!selectedCurriculum) {
      setCurriculumError("Please select a curriculum.");
      hasError = true;
    }

    // Validate that end date is after start date
    const startDate = new Date(periodStart);
    const endDate = new Date(periodEnd);
    
    if (endDate < startDate) {
      setDateError("End date must be after start date");
      hasError = true;
      return;
    }

    if (hasError) {
      return;
    }

    // Convert dates to RFC3339 format with appropriate times and timezone
    const startDateTime = new Date(periodStart);
    startDateTime.setHours(0, 0, 0, 0);
    
    const endDateTime = new Date(periodEnd);
    endDateTime.setHours(23, 59, 59, 999);

    // Get timezone offset in hours:minutes format
    const offsetMinutes = startDateTime.getTimezoneOffset();
    const offsetHours = Math.abs(Math.floor(offsetMinutes / 60)).toString().padStart(2, "0");
    const offsetMins = Math.abs(offsetMinutes % 60).toString().padStart(2, "0");
    const offsetSign = offsetMinutes <= 0 ? "+" : "-";

    // Format dates with timezone, ensuring we use the selected dates
    const formattedStartDate = `${periodStart}T00:00:00${offsetSign}${offsetHours}:${offsetMins}`;
    const formattedEndDate = `${periodEnd}T23:59:59${offsetSign}${offsetHours}:${offsetMins}`;

    try {
      const response = await fetch(`${API_BASE_URL}/api/snippets/generate`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
          "X-Org-ID": selectedOrgID?.toString() || "",
        },
        body: JSON.stringify({
          group_id: parseInt(group_id || "0", 10),
          curriculum_id: parseInt(selectedCurriculum, 10),
          period_start: formattedStartDate,
          period_end: formattedEndDate,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        setError(errorData.error || "Failed to generate snippets.");
        return;
      }

      navigate(`/groups/${group_id}`);
    } catch (err) {
      setError("An unexpected error occurred.");
    }
  };

  return (
    <div className={styles.container}>
      <h1 className={styles.title}>Generate Snippets</h1>
      
      {error && <p className={styles.error}>{error}</p>}

      <div className={styles.groupDetails}>
        <strong>Group:</strong>{" "}
        <Link to={`/groups/${group_id}`} className={styles.groupLink}>
          {groupName}
        </Link>
      </div>

      <div className={styles.formGroup}>
        <label className={styles.label}>Curriculum:</label>
        <select
          value={selectedCurriculum}
          onChange={(e) => setSelectedCurriculum(e.target.value)}
          className={styles.select}
        >
          <option value="">Select a curriculum</option>
          {curriculums.map((curriculum) => (
            <option key={curriculum.curriculum_id} value={curriculum.curriculum_id}>
              {curriculum.name}
            </option>
          ))}
        </select>
      </div>

      <div className={styles.formGroup}>
        <label className={styles.label}>Period Start:</label>
        <input
          type="date"
          value={periodStart}
          onChange={(e) => setPeriodStart(e.target.value)}
          className={styles.input}
        />
      </div>

      <div className={styles.formGroup}>
        <label className={styles.label}>Period End:</label>
        <input
          type="date"
          value={periodEnd}
          onChange={(e) => setPeriodEnd(e.target.value)}
          className={styles.input}
        />
      </div>

      {dateError && <p className={styles.error}>{dateError}</p>}
      {curriculumError && <p className={styles.error}>{curriculumError}</p>}

      <button
        onClick={handleSubmit}
        className={styles.submitButton}
        disabled={!selectedCurriculum || !periodStart || !periodEnd}
      >
        Generate Snippets
      </button>
    </div>
  );
};

export default GroupSnippetsPage; 